
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { useState, useEffect } from "react";
import { createLogger } from "@/lib/utils/logger";

import SystemMetricsCards from "@/components/tech/SystemMetricsCards";
import PerformanceChart from "@/components/tech/PerformanceChart";
import UserActivityChart from "@/components/tech/UserActivityChart";
import ErrorLogsDisplay from "@/components/tech/ErrorLogsDisplay";

// Create a logger for the SystemMonitor component
const logger = createLogger('system', 'SystemMonitor');

export type SystemMetric = {
  id: string;
  timestamp: string;
  cpu_usage: number;
  memory_usage: number;
  request_count: number;
  active_users: number;
  new_users: number;
  database_response_time: number;
  metric_date: string;
  uptime_percentage?: number;
}

export type SystemLog = {
  id: string;
  timestamp: string;
  level: 'error' | 'warning' | 'info' | 'debug';
  source: string;
  message: string;
  details: string | null;
  component: string | null;
  status: 'unresolved' | 'investigating' | 'resolved';
}

export default function SystemMonitor() {
  const [activeTab, setActiveTab] = useState("performance");
  const [systemStatus, setSystemStatus] = useState<'operational' | 'degraded' | 'critical'>('operational');
  const [dataInitialized, setDataInitialized] = useState(false);
  const [isTestData, setIsTestData] = useState(false);

  const { 
    data: performanceData, 
    isLoading: isLoadingPerformance,
    refetch: refetchPerformance,
    error: performanceError,
    isSuccess: isPerformanceSuccess,
  } = useQuery({
    queryKey: ['system-metrics', 'hourly'],
    queryFn: async () => {
      logger.info('Fetching hourly system metrics');
      console.log('Fetching hourly metrics...');
      const { data, error } = await supabase
        .from('system_metrics')
        .select('*')
        .like('metric_date', '%:%')
        .order('timestamp', { ascending: true });
        
      if (error) {
        logger.error(`Error fetching system metrics: ${error.message}`, error.details);
        console.error("Error fetching system metrics:", error);
        toast({
          title: "Error loading system metrics",
          description: error.message,
          variant: "destructive"
        });
        throw new Error(error.message);
      }
      
      // Log the data we retrieved
      console.log('Hourly performance data retrieved:', data?.length || 0, 'records');
      
      // If no data is returned, add a log entry and try to generate some
      if (!data || data.length === 0) {
        logger.warning('No hourly system metrics found in database', 'Performance data query returned no results');
        
        // Try to execute the function to generate metrics
        try {
          console.log('Attempting to generate system metrics...');
          setIsTestData(true);
          const { error: genError } = await supabase.rpc('generate_system_metrics');
          
          if (genError) {
            console.error('Error generating metrics:', genError);
          } else {
            console.log('Successfully generated metrics, refetching...');
            // Retry the fetch after generating
            const { data: newData, error: newError } = await supabase
              .from('system_metrics')
              .select('*')
              .like('metric_date', '%:%')
              .order('timestamp', { ascending: true });
              
            if (!newError && newData && newData.length > 0) {
              return newData as SystemMetric[];
            }
          }
        } catch (genErr) {
          console.error('Exception while generating metrics:', genErr);
        }
        
        await supabase.from('system_logs').insert([{
          level: 'warning',
          source: 'system',
          message: 'No hourly system metrics found in database',
          details: 'System Monitor attempted to fetch metrics but none were found',
          component: 'SystemMonitor',
          status: 'unresolved'
        }]);
        
        return [] as SystemMetric[];
      } else {
        // Check if this is likely test data by checking the number of records
        // This is a basic heuristic - you may want to add a more robust check
        if (data.length === 24 && data.some(metric => metric.active_users > 30)) {
          setIsTestData(true);
        }
        logger.info(`Retrieved ${data.length} hourly system metrics`);
      }
      
      return data as SystemMetric[];
    },
    retry: 1,
    staleTime: 60 * 1000, // 1 minute
  });

  const { 
    data: userActivityData, 
    isLoading: isLoadingActivity,
    refetch: refetchActivity,
    error: activityError,
    isSuccess: isActivitySuccess,
  } = useQuery({
    queryKey: ['system-metrics', 'weekly'],
    queryFn: async () => {
      logger.info('Fetching weekly user activity metrics');
      console.log('Fetching weekly metrics...');
      const { data, error } = await supabase
        .from('system_metrics')
        .select('*')
        .not('metric_date', 'like', '%:%')
        .order('timestamp', { ascending: true });
        
      if (error) {
        logger.error(`Error fetching user activity: ${error.message}`, error.details);
        console.error("Error fetching user activity:", error);
        toast({
          title: "Error loading user activity data",
          description: error.message,
          variant: "destructive"
        });
        throw new Error(error.message);
      }
      
      // Log the data we retrieved
      console.log('Weekly activity data retrieved:', data?.length || 0, 'records');
      
      // If no data is returned, try to generate metrics first
      if (!data || data.length === 0) {
        logger.warning('No weekly user activity metrics found in database', 'User activity query returned no results');
        
        // Try to execute the function to generate metrics if not already tried
        if (!dataInitialized) {
          try {
            console.log('Attempting to generate system metrics for user activity...');
            setIsTestData(true);
            const { error: genError } = await supabase.rpc('generate_system_metrics');
            
            if (genError) {
              console.error('Error generating metrics:', genError);
            } else {
              console.log('Successfully generated metrics, refetching...');
              setDataInitialized(true);
              
              // Retry the fetch after generating
              const { data: newData, error: newError } = await supabase
                .from('system_metrics')
                .select('*')
                .not('metric_date', 'like', '%:%')
                .order('timestamp', { ascending: true });
                
              if (!newError && newData && newData.length > 0) {
                return newData as SystemMetric[];
              }
            }
          } catch (genErr) {
            console.error('Exception while generating metrics:', genErr);
          }
        }
        
        await supabase.from('system_logs').insert([{
          level: 'warning',
          source: 'system',
          message: 'No weekly user activity metrics found in database',
          details: 'System Monitor attempted to fetch metrics but none were found',
          component: 'SystemMonitor',
          status: 'unresolved'
        }]);
        
        return [] as SystemMetric[];
      } else {
        // Check if this is likely test data
        if (data.length === 7 && data.some(metric => metric.active_users > 30)) {
          setIsTestData(true);
        }
        logger.info(`Retrieved ${data.length} weekly user activity metrics`);
      }
      
      return data as SystemMetric[];
    },
    retry: 1,
    staleTime: 60 * 1000, // 1 minute
  });

  const { 
    data: errorLogData, 
    isLoading: isLoadingErrors,
    refetch: refetchErrors,
    error: errorLogsError,
  } = useQuery({
    queryKey: ['system-logs'],
    queryFn: async () => {
      logger.info('Fetching system error logs');
      console.log('Fetching system logs...');
      const { data, error } = await supabase
        .from('system_logs')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(10);
        
      if (error) {
        logger.error(`Error fetching system logs: ${error.message}`, error.details);
        console.error("Error fetching system logs:", error);
        toast({
          title: "Error loading system logs",
          description: error.message,
          variant: "destructive"
        });
        throw new Error(error.message);
      }
      
      // Log the data we retrieved
      console.log('System logs retrieved:', data?.length || 0, 'records');
      
      return data as SystemLog[];
    }
  });

  // Get the latest metrics from the database
  const getLatestMetrics = () => {
    if (!performanceData || performanceData.length === 0) {
      logger.info('No performance data available for metrics cards');
      // No data available, return default values
      return {
        cpu: 0,
        memory: 0,
        dbResponseTime: 0,
        uptime: 99.5 // Default uptime to prevent critical state when no data
      };
    }

    // Use actual data from the database
    const latest = performanceData[performanceData.length - 1];
    console.log('Latest performance metrics:', latest);
    
    // Calculate uptime based on system health if not provided
    const uptimeValue = latest.uptime_percentage || 
      (100 - ((latest.cpu_usage + latest.memory_usage) / 4));
    const normalizedUptime = Math.min(99.99, Math.max(95, uptimeValue)).toFixed(1);
    
    return {
      cpu: latest.cpu_usage,
      memory: latest.memory_usage,
      dbResponseTime: latest.database_response_time,
      uptime: parseFloat(normalizedUptime)
    };
  };

  // Get current active users directly from the database
  const getCurrentActiveUsers = () => {
    if (!userActivityData || userActivityData.length === 0) {
      logger.info('No user activity data available for active users count');
      return 0;
    }
    
    // Return the actual value from the database
    const latestActivity = userActivityData[userActivityData.length - 1];
    console.log('Latest active users:', latestActivity.active_users);
    return latestActivity.active_users;
  };

  // Calculate average session time based on data
  const calculateAverageSessionTime = () => {
    if (!userActivityData || userActivityData.length === 0) {
      logger.info('No user activity data available for session time calculation');
      return 0;
    }
    
    console.log('Calculating average session time based on:', userActivityData.length, 'records');
    
    // Get the data for calculation
    const totalUsers = userActivityData.reduce((sum, item) => sum + item.active_users, 0);
    const totalRequests = userActivityData.reduce((sum, item) => sum + item.request_count, 0);
    
    console.log('Total users across all data points:', totalUsers);
    console.log('Total requests across all data points:', totalRequests);
    
    // If we don't have enough data to calculate, return a default value
    if (totalUsers === 0 || totalRequests === 0) {
      logger.warning('Cannot calculate average session time: insufficient data', 
        `totalUsers: ${totalUsers}, totalRequests: ${totalRequests}`);
      console.log('Cannot calculate average session time: insufficient data');
      return 0;
    }
    
    // Simple calculation that's transparent about its assumptions
    const avgRequestsPerUser = totalRequests / totalUsers;
    
    // Instead of hardcoding 2 minutes, use a more realistic estimate
    // and adjust based on your actual application behavior
    const minutesPerRequest = 2; // Assumption: each request takes about 2 minutes
    const avgMinutesPerSession = Math.round(avgRequestsPerUser * minutesPerRequest);
    
    console.log('Average requests per user:', avgRequestsPerUser);
    console.log('Calculated avg minutes per session:', avgMinutesPerSession);
    
    return avgMinutesPerSession;
  };

  // Update system status based on metrics
  useEffect(() => {
    // Only update status when performance data is available
    if (isPerformanceSuccess && performanceData && performanceData.length > 0) {
      const metrics = getLatestMetrics();
      
      if (metrics.cpu > 80 || metrics.memory > 80 || metrics.dbResponseTime > 250 || metrics.uptime < 99) {
        setSystemStatus('critical');
      } else if (metrics.cpu > 60 || metrics.memory > 60 || metrics.dbResponseTime > 150 || metrics.uptime < 99.8) {
        setSystemStatus('degraded');
      } else {
        setSystemStatus('operational');
      }
    }
  }, [performanceData, isPerformanceSuccess]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    logger.info('Setting up auto-refresh interval');
    const intervalId = setInterval(() => {
      logger.info('Auto-refreshing data');
      console.log('Auto-refreshing performance and error data...');
      refetchPerformance();
      refetchActivity();
      refetchErrors();
    }, 5 * 60 * 1000);
    
    return () => {
      logger.info('Clearing auto-refresh interval');
      clearInterval(intervalId);
    };
  }, [refetchPerformance, refetchActivity, refetchErrors]);

  // Log when the component mounts and initial data fetch
  useEffect(() => {
    logger.info('SystemMonitor component mounted');
    console.log('SystemMonitor component mounted');
    
    // Check if we need to initialize data
    if (!dataInitialized && !isLoadingPerformance && (!performanceData || performanceData.length === 0)) {
      console.log('No performance data found, will attempt to generate metrics when queries run');
    }
  }, []);

  // Memoize derived values to prevent unnecessary calculations
  const latestMetrics = getLatestMetrics();
  const activeUsers = getCurrentActiveUsers();
  const avgSessionTime = calculateAverageSessionTime();
  
  console.log('Current metrics being passed to components:');
  console.log('- Latest metrics:', latestMetrics);
  console.log('- Active users:', activeUsers);
  console.log('- Avg session time:', avgSessionTime);

  return (
    <div className="container mx-auto pt-6 px-6 space-y-6 max-w-7xl animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-title font-bold tracking-tight text-primary">System Monitor</h1>
          <div className="flex items-center gap-2">
            <p className="text-muted-foreground font-subtitle">Real-time performance metrics and diagnostics</p>
            <Badge 
              className={`ml-2 ${
                systemStatus === 'operational' ? 'bg-green-500' : 
                systemStatus === 'degraded' ? 'bg-amber-500' : 
                'bg-red-500'
              }`}
            >
              {systemStatus === 'operational' ? 'Operational' : 
               systemStatus === 'degraded' ? 'Degraded' : 
               'Critical'}
            </Badge>
            
            {isTestData && (
              <Badge variant="outline" className="ml-2 border-dashed">
                <AlertCircle className="h-3.5 w-3.5 mr-1" />
                Demo Data
              </Badge>
            )}
          </div>
        </div>
        <Button className="gap-2" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4" />
          Refresh Data
        </Button>
      </div>

      {isTestData && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-sm">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Viewing Demonstration Data</p>
                <p className="text-amber-700 mt-1">
                  The metrics shown are synthetic test data generated by the system. The calculated values 
                  (like 88 active users and 4 minutes average session time) are based on this random data 
                  and do not reflect actual usage patterns. Your system has fewer than 30 registered users.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <SystemMetricsCards 
        latestMetrics={latestMetrics} 
        isLoading={isLoadingPerformance} 
      />

      <Tabs defaultValue="performance" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="font-subtitle">
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="users">User Activity</TabsTrigger>
          <TabsTrigger value="errors">Error Logs</TabsTrigger>
        </TabsList>
        
        <TabsContent value="performance" className="space-y-4">
          {(!performanceData || performanceData.length === 0) && !isLoadingPerformance ? (
            <Card>
              <CardContent className="p-6">
                <p className="text-center text-muted-foreground">No performance data available in the database. Please ensure system metrics are being collected.</p>
              </CardContent>
            </Card>
          ) : (
            <PerformanceChart 
              data={performanceData || []} 
              isLoading={isLoadingPerformance} 
            />
          )}
        </TabsContent>
        
        <TabsContent value="users" className="space-y-4">
          {(!userActivityData || userActivityData.length === 0) && !isLoadingActivity ? (
            <Card>
              <CardContent className="p-6">
                <p className="text-center text-muted-foreground">No user activity data available in the database. Please ensure user metrics are being collected.</p>
              </CardContent>
            </Card>
          ) : (
            <UserActivityChart 
              data={userActivityData || []} 
              isLoading={isLoadingActivity}
              activeUsers={activeUsers}
              avgSessionTime={avgSessionTime}
            />
          )}
        </TabsContent>
        
        <TabsContent value="errors" className="space-y-4">
          <ErrorLogsDisplay 
            data={errorLogData} 
            isLoading={isLoadingErrors} 
          />
        </TabsContent>
      </Tabs>
    </div>
  );

  // Handle refresh button click
  function handleRefresh() {
    logger.info(`Manually refreshing ${activeTab} data`);
    toast({
      title: "Refreshing data",
      description: "Fetching latest system metrics and logs...",
    });
    
    if (activeTab === "performance") {
      refetchPerformance();
    } else if (activeTab === "users") {
      refetchActivity();
    } else if (activeTab === "errors") {
      refetchErrors();
    }
  }
}
